import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Tag } from "@/components/common/StatusChip";

const TONE = { received: "neutral", held: "amber", queued: "blue", accepted: "cyan", confirmed: "emerald", unconfirmed: "amber", failed: "red", dead_letter: "red" };
const RETRYABLE = ["held", "failed", "dead_letter", "received"];

export default function RetellRequests() {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery({ queryKey: ["retell-requests"], queryFn: () => api.get("/retell/requests?limit=30").then((r) => r.data), refetchInterval: 15000 });
  const retry = async (r) => {
    try { const { data } = await api.post(`/retell/requests/${r.id}/retry`); toast.success(`${r.reference} → ${data.status}`); qc.invalidateQueries({ queryKey: ["retell-requests"] }); qc.invalidateQueries({ queryKey: ["retell-health"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4" data-testid="retell-requests">
      <div className="eyebrow mb-1">Recent Ava requests</div>
      {rows.length === 0 && <p className="text-xs text-muted-foreground" data-testid="retell-requests-empty">No requests from Ava yet.</p>}
      {rows.length > 0 && (
        <div className="overflow-x-auto border border-border">
          <table className="w-full text-[11px]">
            <thead><tr className="border-b border-border bg-muted/40 text-left">{["When", "Reference", "Caller", "Type", "Priority", "Status", "Ticket", ""].map((h) => <th key={h} className="eyebrow px-2 py-1.5">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`retell-row-${r.reference}`}>
                  <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="mono px-2 py-1.5">{r.reference}{r.is_test && <Tag tone="neutral" className="ml-1">test</Tag>}</td>
                  <td className="px-2 py-1.5">{r.fields?.customer_name}<div className="mono text-muted-foreground">{r.fields?.callback_number}</div></td>
                  <td className="px-2 py-1.5">{r.fields?.request_type}</td>
                  <td className="px-2 py-1.5"><Tag tone={r.priority === "URGENT" ? "red" : "neutral"}>{r.priority}</Tag></td>
                  <td className="px-2 py-1.5"><Tag tone={TONE[r.status] || "neutral"} data-testid={`retell-status-${r.reference}`}>{r.status.replace("_", " ")}</Tag>{r.alerts?.length > 0 && <div className="text-amber-500">alerted</div>}</td>
                  <td className="mono px-2 py-1.5">{r.ticket_id ? <Link to={`/tickets/${r.ticket_id}`} className="text-primary hover:underline">{r.ticket_number}</Link> : "—"}</td>
                  <td className="px-2 py-1.5">{RETRYABLE.includes(r.status) && <button className="inline-flex items-center gap-1 text-primary hover:underline" onClick={() => retry(r)} data-testid={`retell-retry-${r.reference}`}><RotateCcw className="h-3 w-3" /> send</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
